/**
 * 上传受理规则的管端镜像。
 *
 * 权威在服务端的 `src/upload-contract.ts`；这里是它的一份拷贝，
 * **由 upload.test.ts 逐条比对，改一边不改另一边会红**。
 *
 * 为什么是镜像而不是直接 import：web 的 tsconfig 只 include `web/src`，
 * 跨目录引用要动 vite alias 与 include——那份配置里全是微前端白屏的坑，
 * 为一个常量表去动它不划算。用测试锁住比用构建配置锁住便宜。
 *
 * 这份东西存在的理由：规则原本散在每个对话框的 `accept="…"` 里，
 * AssetDialog 曾经多列了 `image/gif`——用户能选中、2MB 整个传完、
 * 然后被服务端拒掉，提示还只说「只支持 png / jpg / webp」。
 */

export const UPLOAD_EXT_BY_TYPE: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

/**
 * 大小上限。50MB 不是「怕大」，是**平台硬顶之下的一个诚实落点**：
 * 服务端把整个 body 读成 ArrayBuffer 再转发，Cloudflare Worker 的请求体与内存
 * 都有硬限制，真让它无上限，超限时用户拿到的是 413 / 网络错误这类看不懂的东西，
 * 比一句「不能超过 50MB」还难排查。4K 背景 PNG 一般 5~15MB，50MB 实际用不到
 * （leo 2026-08-28：2MB 太小，不要限制）。
 */
export const UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

/** 给 <input type="file" accept> 用 */
export const UPLOAD_ACCEPT = Object.keys(UPLOAD_EXT_BY_TYPE).join(",");

export const UPLOAD_MAX_LABEL = `${UPLOAD_MAX_BYTES / 1024 / 1024}MB`;

/** 拒绝原因：i18n key + 插值变量。**不返回成品文案**——管端有中英两套界面，
 * 写死中文会让英文后台弹中文（codex 复审 2026-08-28）。服务端那份（src/upload-contract.ts）
 * 仍返回中文字符串：它是 API 错误消息，给调用方看，不进界面。 */
export type UploadReject = {
  key: "upload.badType" | "upload.empty" | "upload.tooBig" | "upload.badName" | "upload.badExt"
    | "upload.video.badType" | "upload.video.tooBig";
  vars?: Record<string, string>;
};

/**
 * 传出去之前先自查。返回 null = 可以传；否则给出拒绝原因，由调用方翻译成当前界面语言。
 * 服务端会独立再查一次——这里查只是为了省掉一趟必然失败的上传，不是防线。
 */
export function uploadRejectReason(type: string, size: number): UploadReject | null {
  if (!UPLOAD_EXT_BY_TYPE[type]) {
    return { key: "upload.badType", vars: { kinds: Object.values(UPLOAD_EXT_BY_TYPE).join(" / "), type: type || "" } };
  }
  if (size === 0) return { key: "upload.empty" };
  if (size > UPLOAD_MAX_BYTES) {
    return { key: "upload.tooBig", vars: { max: UPLOAD_MAX_LABEL, size: (size / 1024 / 1024).toFixed(1) } };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// VIP 视频（mp4）
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 视频只在这一个上传位收，图片位一律不收（权威在服务端 src/upload-contract.ts，由 upload.test.ts 比对）。
 * 格式（faststart / 编码 / 时长）另由 lib/mp4-inspect.ts 判——那份与服务端逐字节相同。
 */
export const VIDEO_UPLOAD_MODULE = "idol-vip-video";

export const VIDEO_EXT_BY_TYPE: Record<string, string> = {
  "video/mp4": "mp4",
};

export const VIDEO_UPLOAD_ACCEPT = Object.keys(VIDEO_EXT_BY_TYPE).join(",");

/** 视频的类型 + 大小自查。大小上限和图片同一个（服务端整包读进内存再转发） */
export function videoUploadRejectReason(type: string, size: number): UploadReject | null {
  if (!VIDEO_EXT_BY_TYPE[type]) return { key: "upload.video.badType", vars: { type: type || "" } };
  if (size === 0) return { key: "upload.empty" };
  if (size > UPLOAD_MAX_BYTES) {
    return { key: "upload.video.tooBig", vars: { max: UPLOAD_MAX_LABEL, size: (size / 1024 / 1024).toFixed(1) } };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// 邀请落地页（多收 AVIF）
// ─────────────────────────────────────────────────────────────────────────────

/** 落地页的图多收 AVIF（只在网页显示）；游戏图片位不收，Unity 解不了（权威在服务端 src/upload-contract.ts） */
export const LANDING_UPLOAD_MODULE = "landing";

export const LANDING_EXT_BY_TYPE: Record<string, string> = { ...UPLOAD_EXT_BY_TYPE, "image/avif": "avif" };

export const LANDING_UPLOAD_ACCEPT = Object.keys(LANDING_EXT_BY_TYPE).join(",");

export function landingUploadRejectReason(type: string, size: number): UploadReject | null {
  if (!LANDING_EXT_BY_TYPE[type]) return { key: "upload.badType", vars: { kinds: Object.values(LANDING_EXT_BY_TYPE).join(" / "), type: type || "" } };
  return uploadRejectReason(type === "image/avif" ? "image/webp" : type, size);
}

// ─────────────────────────────────────────────────────────────────────────────
// 按文件名受理（Spine 等非图片素材）
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 上面那套按 MIME 判的规则对 Spine 一条都用不上：`.skel` / `.atlas` 浏览器认不出，
 * `file.type` 是空串。所以这一套**按文件名后缀判**。
 * 权威同样在服务端的 `src/upload-contract.ts`，由 upload.test.ts 逐条比对。
 */
/**
 * 一组 Spine 的三个文件：骨骼、图集、贴图。`.meta` 不在里面（leo 2026-09-08 去掉）——
 * 那是 Cocos 编辑器的资产库文件，运行时用不上。顺序 = 管端槽位列表的展示顺序。
 */
export const UPLOAD_NAMED_EXTS = [".skel", ".atlas", ".png"] as const;

export const UPLOAD_NAMED_ACCEPT = UPLOAD_NAMED_EXTS.join(",");

const NAMED_FILE_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,95}$/;

/**
 * 按文件名受理时的自查。只判这一个文件本身：**不判数量、不判后缀齐不齐**
 * —— 那两条是页面的事（见 lib/pet.ts 的 spineGroupIssue）。
 */
export function namedFileRejectReason(name: string, size: number): UploadReject | null {
  if (!NAMED_FILE_RE.test(name) || name.includes("..")) return { key: "upload.badName", vars: { name } };
  if (!UPLOAD_NAMED_EXTS.some((e) => name.toLowerCase().endsWith(e))) {
    return { key: "upload.badExt", vars: { name, exts: UPLOAD_NAMED_EXTS.join(" / ") } };
  }
  if (size === 0) return { key: "upload.empty" };
  if (size > UPLOAD_MAX_BYTES) {
    return { key: "upload.tooBig", vars: { max: UPLOAD_MAX_LABEL, size: (size / 1024 / 1024).toFixed(1) } };
  }
  return null;
}

/**
 * 这个文件名占哪个 Spine 后缀槽位；不属于任何一个返回 null。
 * 服务端权威在 `src/upload-contract.ts` 的 spineSlotOf，由 upload.test.ts 比对。
 *
 * **先长后短**：当前三个后缀互不为前缀，谁先谁后都一样；留着排序是为了以后
 * 往清单里加值时不用重新想一遍（曾经有过 `.png.meta`，那时这行是必须的）。
 */
export function spineSlotOf(name: string): string | null {
  const lower = name.toLowerCase();
  return [...UPLOAD_NAMED_EXTS].sort((a, b) => b.length - a.length).find((e) => lower.endsWith(e)) ?? null;
}

/** 一组动画素材缺哪些槽位、哪些传重了。两边都空 = 齐了。只看后缀，不看数量 */
export function spineGroupGaps(names: string[]): { missing: string[]; dup: string[] } {
  const count = new Map<string, number>();
  for (const n of names) {
    const slot = spineSlotOf(n);
    if (slot) count.set(slot, (count.get(slot) ?? 0) + 1);
  }
  return {
    missing: UPLOAD_NAMED_EXTS.filter((e) => !count.has(e)),
    dup: UPLOAD_NAMED_EXTS.filter((e) => (count.get(e) ?? 0) > 1),
  };
}
